/* eslint-disable @typescript-eslint/no-explicit-any */
import { PropsWithChildren, useContext, Context } from "react";
import { Provider, MobXProviderContext } from "mobx-react";
import { MapStorevalue, ClassLike } from "./helper";

export default function createStore<S extends Record<string, ClassLike<any>>, P = any>(
	storeMaps: S,
) {
	let store: MapStorevalue<S> & { _hydrate: (initialData: P) => void };
	class Store {
		public constructor(stores: S) {
			Object.keys(stores).forEach((item) => {
				this[item] = new stores[item]();
			});

			this._hydrate = (initialData: P) => {
				Object.keys(stores).forEach((item) => {
					if (typeof this[item]?.hydrate === "function") {
						this[item].hydrate(initialData);
					}
				});
			};
		}

		public _hydrate: (initialData: P) => void;
	}

	function initializeStore(initialData = null) {
		const _store =
			store ??
			(new Store(storeMaps) as unknown as MapStorevalue<S> & {
				_hydrate: (initialData: any) => void;
			});

		if (initialData) {
			_store._hydrate(initialData);
		}

		if (typeof window === "undefined") return _store;
		if (!store) store = _store;

		return _store;
	}

	function useStore() {
		const context = useContext(MobXProviderContext as Context<MapStorevalue<S>>);
		if (context === undefined) {
			throw new Error("useStore must be used within StoreProvider");
		}

		return context;
	}

	function StoreProvider({ children, ...restProps }: PropsWithChildren<P>) {
		const store = initializeStore(restProps);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { _hydrate, ...restStore } = store || {};
		return <Provider {...restStore}>{children}</Provider>;
	}

	return {
		Provider: StoreProvider,
		useStore,
	};
}
